import { useAuth } from '@/auth/auth-context';
import { DEFAULT_TIMEZONE, platformTimezone, setPlatformTimezone } from './time';

/**
 * The platform timezone, taken from the server and pushed into `time.ts`.
 *
 * It arrives on `/auth/me`, which this panel already fetches before it draws
 * anything, so it costs no extra request and is in place before the first date
 * is rendered.
 *
 * **There is no setter, and that is the point.** An earlier version of this
 * stored the zone in `app_config` and let this panel write it — which created
 * two sources of truth for one value, because the backend reads its own
 * `PLATFORM_TIMEZONE` environment variable when it decides whether a shop is
 * open. The two would drift, and the drift would be invisible: the panel would
 * show a schedule the marketplace was not acting on.
 *
 * It lives in the environment rather than in the config table for the same
 * reason `settings.api.ts` gives for keeping throttles off `/admin/config/:key`
 * — a wrong value here does not announce itself, it just shifts every shop's
 * trading hours by a few hours. It is deployment infrastructure, it changes
 * about once in the life of the platform, and the server needs it whether or
 * not any panel is running.
 */
export function usePlatformTimezoneSync(): string {
  const { user } = useAuth();
  const zone = user?.platformTimezone ?? DEFAULT_TIMEZONE;

  // Applied during render on purpose: an effect would let the first paint of
  // every page below this one go out on the previous zone.
  if (zone !== platformTimezone()) setPlatformTimezone(zone);

  return platformTimezone();
}

/** The same value, for a screen that only wants to read it. */
export function usePlatformTimezone(): string {
  return usePlatformTimezoneSync();
}
